import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Bell, UserCircle, LogOut, Star, MessageCircle } from 'lucide-react';
import axios from 'axios';

const Header = () => {
    const { user, logout, token } = useAuth();
    const navigate = useNavigate();
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        const fetchNotifications = async () => {
            if (!token) return;
            try {
                const res = await axios.get('/api/notifications', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const count = res.data.filter(n => !n.read).length;
                setUnreadCount(count);
            } catch (error) {
                console.error('Failed to fetch notifications status:', error);
            }
        };
        if (token) {
            fetchNotifications();
            const interval = setInterval(fetchNotifications, 30000);
            return () => clearInterval(interval);
        }
    }, [token]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-40 p-4">
            <motion.div 
                className="w-full max-w-2xl mx-auto bg-gray-900/40 backdrop-blur-xl border border-white/10 shadow-lg rounded-2xl flex items-center justify-between p-3"
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 70, damping: 20 }}
            >
                {/* Logo */}
                <Link to="/" className="flex items-center gap-2 text-white">
                     <div className="flex items-center justify-center bg-cyan-500/20 p-2 rounded-lg">
                        <Star className="h-6 w-6 text-cyan-400" />
                    </div>
                    <span className="text-xl font-bold tracking-tight">RatingApp</span>
                </Link>

                {/* Navigation Icons */}
                <div className="flex items-center gap-4">
                    <a
                        href="https://wa.me/447763268572"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-full text-green-400 hover:text-green-300 hover:bg-green-500/10 transition-colors"
                        aria-label="Contact on WhatsApp"
                        title="WhatsApp"
                    >
                        <MessageCircle size={22} />
                    </a>
                    <Link to="/notification" className="p-2 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition-colors relative">
                        <Bell size={22} />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 bg-red-500 text-white text-xs rounded-full animate-pulse">
                                {unreadCount}
                            </span>
                        )}
                    </Link>
                    
                    <Link to="/profile" className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors">
                        {user?.profilePictureUrl ? (
                            <img src={user.profilePictureUrl} alt="Profile" className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                             <UserCircle size={24} />
                        )}
                        <span className="hidden sm:inline text-sm font-medium">{user?.name || 'My Profile'}</span>
                    </Link>

                    <button 
                        onClick={handleLogout} 
                        className="flex items-center gap-2 py-2 px-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 rounded-lg transition-colors"
                    >
                        <LogOut size={18} />
                        <span className="hidden sm:inline text-sm font-semibold">Logout</span>
                    </button>
                </div>
            </motion.div>
        </header>
    );
};

export default Header;
